<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext';
    import { cx } from '$lib/utils/cx';
    import { createPopover, melt } from '@melt-ui/svelte';
    import type { NavbarQueryQuery } from '$gql/graphql';
    import * as t from '$trans';
    import { transitionIn, transitionOut } from './utils';
    import { CountryNull, CountryUseCurrent, slugify } from '$lib/UrlHelper';
    import { type Service, services } from '$components/Navbar/services';
    import GridDropdownItem from '$components/Navbar/GridDropdownItem.svelte';
    import { roundedIcon } from '$components/CountryPicture.svelte';
    import { match } from 'ts-pattern';
    import MainButtonMobile_ModalButton from '$components/Navbar/MainButtonMobile_ModalButton.svelte';
    import entreprises_png from '$assets/super-app/entreprises_h128.png';
    import professionnels_png from '$assets/super-app/professionnels_h128.png';
    import reseau_social_png from '$assets/super-app/reseau_social_h128.png';
    import messagerie_privee_png from '$assets/super-app/messagerie_privee_h128.png';
    import emploi_png from '$assets/super-app/emploi_h128.png';
    import medias_png from '$assets/super-app/medias_h128.png';

    export let navbar: NavbarQueryQuery;
    export let navbarCountryLink: { withCountry: string; withoutCountry: string } | null;

    const { urlHelper, intl, dpapHelper, countryHelper, svelteClient, lang } =
        getContainer();
    const currentCountry = countryHelper().currentCountry();

    const {
        elements: { trigger, content },
        states: { open },
    } = createPopover({
        defaultOpen: false,
        positioning: {
            flip: false,
            overflowPadding: 0,
            fitViewport: true,
            strategy: 'fixed',
            gutter: 0,
            placement: 'bottom',
        },
    });

    svelteClient.afterNavigate(async (navigation) => {
        await navigation.complete;
        $open = false;
    });

    const sepClass = 'mx-[16px] my-[16px] h-[1px] bg-[#408AD9]';
    const linkClass = cx(
        'btn-reset outline-0 text-left',
        'hover:bg-brand-blue/50 focus:bg-brand-blue/50 focus:ring focus:ring-inset',
        'px-[16px] py-[12px]',
        'text-18/[120%] text-white font-medium',
        'no-underline',
    );

    function serviceLink(service: Service) {
        return match(service.link)
            .with('cmsCountry', () => navbar.urls.cmsCountry)
            .otherwise((link) => link(urlHelper()));
    }
</script>

<button
    use:melt={$trigger}
    class={cx(
        'h-full',
        'px-[8px]',
        'flex items-center justify-center',
        'btn-reset',
        'text-white',
        'bg-brand-blue hover:bg-brand-navbar-blue-alt focus:bg-brand-navbar-blue-alt',
        'outline-0',
    )}
>
    <span class="sr-only">{t.navbar.menu({}, $lang)}</span>
    <i class="text-24 tnp tnp-menu-burger" aria-hidden={true} />
</button>

{#if $open}
    <section
        use:melt={$content}
        class={cx(
            'p-0 m-0 overflow-hidden bg-brand-navbar-blue-alt shadow-dropdown z-navbar-minus-1',
            'h-[calc(100vh-60px)]',
            'w-screen',
            'overflow-x-hidden',
            'overflow-y-auto',
        )}
        in:transitionIn
        out:transitionOut
    >
        <div class="p-0 h-full flex flex-col">
            <MainButtonMobile_ModalButton
                title={t.navbar.select_country({}, $lang)}
                label={$currentCountry == null
                    ? t.navbar.countries({}, $lang)
                    : intl().countryLabel($currentCountry)}
                icon={$currentCountry == null ? 'tnp tnp-africa' : null}
                iconUrl={$currentCountry != null ? roundedIcon($currentCountry) : null}
            >
                <div class="grid grid-cols-2 mm:grid-cols-3 place-items-center gap-[8px]">
                    <GridDropdownItem
                        variant="bg-dark"
                        href={navbarCountryLink?.withoutCountry ??
                            urlHelper().homepage({ _country: CountryNull })}
                        image={`/next-static/images/flags/rounded-africa.svg`}
                        label={t.navbar.africa({}, $lang)}
                    />
                    {#each [...countryHelper().sortCountries(countryHelper().modalCountries)] as c}
                        <GridDropdownItem
                            variant="bg-dark"
                            href={navbarCountryLink?.withCountry
                                ?.replace('__country__', c.toLowerCase())
                                .replace(
                                    '__country_dp__',
                                    slugify(dpapHelper().replaceDpAp(c, '%dp%')),
                                )
                                .replace(
                                    '__country_ap__',
                                    slugify(dpapHelper().replaceDpAp(c, '%ap%')),
                                ) ?? urlHelper().homepage({ _country: c })}
                            image={roundedIcon(c)}
                            label={intl().countryLabel(c)}
                        />
                    {/each}
                </div>
            </MainButtonMobile_ModalButton>

            <MainButtonMobile_ModalButton
                label={t.navbar.our_services({}, $lang)}
                title={t.navbar.our_services({}, $lang)}
            >
                <div class="grid grid-cols-2 mm:grid-cols-3 place-items-center gap-[8px]">
                    {#each services as s}
                        <GridDropdownItem
                            variant="bg-dark"
                            href={serviceLink(s)}
                            image={s.image}
                            label={s.title}
                        />
                    {/each}
                </div>
            </MainButtonMobile_ModalButton>

            <div class={sepClass} />

            <a
                class={linkClass}
                data-sveltekit-reload={false}
                href={navbar.me
                    ? urlHelper().homepage({ _country: CountryUseCurrent })
                    : urlHelper().explorer({ _country: CountryUseCurrent })}
            >
                {t.navbar.network({}, $lang)}
            </a>

            <a
                class={linkClass}
                data-sveltekit-reload={false}
                href={urlHelper().categories({
                    _country: CountryUseCurrent,
                    lang: $lang,
                })}
            >
                {t.navbar.companies({}, $lang)}
            </a>

            <a
                class={linkClass}
                data-sveltekit-reload={false}
                href={urlHelper().searchUsers({})}
            >
                {t.navbar.talents({}, $lang)}
            </a>

            <a class={linkClass} data-sveltekit-reload href={urlHelper().searchJobs({})}>
                {t.navbar.job({}, $lang)}
            </a>

            <MainButtonMobile_ModalButton
                label={t.navbar.our_services({}, $lang)}
                title={t.navbar.our_services({}, $lang)}
            >
                <div class="grid grid-cols-2 mm:grid-cols-3 place-items-center gap-[8px]">
                    {#each services as s}
                        <GridDropdownItem
                            variant="bg-dark"
                            href={serviceLink(s)}
                            image={s.image}
                            label={s.title}
                        />
                    {/each}
                </div>
            </MainButtonMobile_ModalButton>

            <a
                class={linkClass}
                data-sveltekit-reload={false}
                href={urlHelper().contact({ lang: $lang })}
            >
                {t.navbar.contact({}, $lang)}
            </a>

            <div class={sepClass} />

            <MainButtonMobile_ModalButton
                icon="tnp tnp-apps-9"
                label={t.navbar.super_app({}, $lang)}
                title={t.navbar.super_app({}, $lang)}
            >
                <div class="grid grid-cols-2 mm:grid-cols-3 place-items-center gap-[8px]">
                    <GridDropdownItem
                        variant="bg-dark"
                        href={urlHelper().categories({ lang: $lang })}
                        svelteKitReload={false}
                        image={entreprises_png}
                        label={t.navbar.super_app_company({}, $lang)}
                    />
                    <GridDropdownItem
                        variant="bg-dark"
                        href={urlHelper().searchUsers({})}
                        svelteKitReload={false}
                        image={professionnels_png}
                        label={t.navbar.super_app_pro({}, $lang)}
                    />
                    <GridDropdownItem
                        variant="bg-dark"
                        href={navbar.me == null
                            ? urlHelper().servicePage('services_reseau_social_pro')
                            : urlHelper().homepage({ _country: CountryNull })}
                        image={reseau_social_png}
                        label={t.navbar.super_app_social_network({}, $lang)}
                    />
                    <GridDropdownItem
                        variant="bg-dark"
                        href={navbar.me == null
                            ? urlHelper().servicePage('services_inbox')
                            : urlHelper().inbox()}
                        svelteKitReload={navbar.me == null}
                        image={messagerie_privee_png}
                        label={t.navbar.super_app_messaging({}, $lang)}
                    />
                    <GridDropdownItem
                        variant="bg-dark"
                        href={urlHelper().searchJobs({})}
                        image={emploi_png}
                        label={t.navbar.super_app_job({}, $lang)}
                    />
                    <GridDropdownItem
                        variant="bg-dark"
                        href={navbar.urls.cmsCountry}
                        image={medias_png}
                        label={t.navbar.super_app_media_cms({}, $lang)}
                    />
                </div>
            </MainButtonMobile_ModalButton>
        </div>
    </section>
{/if}
